import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import BillingStatus from "@/components/BillingStatus";
import {
  GraduationCap, ClipboardList, FolderOpen, Tags, CalendarDays,
  Receipt, Users, ArrowRight, Sparkles, PlugZap, CheckCircle2, Leaf,
} from "lucide-react";

const statConfig = [
  { key: "published_courses", label: "Published Courses", sub: (s) => `${s.courses} total`, icon: GraduationCap, to: "/courses", tint: "bg-gold-50 text-gold-600" },
  { key: "quizzes", label: "Quizzes & Surveys", sub: () => "Wellbeing check-ins", icon: ClipboardList, to: "/quizzes", tint: "bg-rose-50 text-rose-500" },
  { key: "resources", label: "Shared Resources", sub: () => "Guides & files", icon: FolderOpen, to: "/resources", tint: "bg-sky-50 text-sky-500" },
  { key: "plans", label: "Active Plans", sub: () => "Pricing & packages", icon: Tags, to: "/plans", tint: "bg-emerald-50 text-emerald-600" },
];

const quickLinks = [
  { to: "/courses", icon: GraduationCap, label: "Courses" },
  { to: "/quizzes", icon: ClipboardList, label: "Quizzes" },
  { to: "/resources", icon: FolderOpen, label: "Resources" },
  { to: "/plans", icon: Tags, label: "Plans" },
  { to: "/appointments", icon: CalendarDays, label: "Appointments" },
  { to: "/invoices", icon: Receipt, label: "Invoices" },
  { to: "/clients", icon: Users, label: "Clients" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });

  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-gold-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-700">
          <span className="h-1.5 w-1.5 rounded-full bg-gold-500" /> Create · Connect · Share
        </div>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-navy-900 sm:text-4xl">
          Welcome back, {firstName} <span className="inline-block">👋</span>
        </h1>
        <p className="mt-2 text-navy-500">Here's an overview of the wellbeing experience you're building.</p>
      </div>

      <BillingStatus />

      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl border border-gold-100/70 bg-gradient-to-br from-[#FBF6EC] via-[#FBF6EC] to-[#F1E7D0] shadow-ambient">
        <img
          src="/leaf-corner.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-4 top-1/2 h-[135%] w-auto -translate-y-1/2 opacity-80 select-none sm:-right-2"
        />
        <div className="relative flex h-64 flex-col justify-center p-8 sm:h-72 sm:p-12">
          <div className="mb-2 flex items-center gap-2 text-gold-600">
            <Leaf className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em]">Your Resilience Journey</span>
          </div>
          <h2 className="font-serif text-4xl font-bold text-navy-900 sm:text-5xl">Owning My Journey</h2>
          <p className="mt-3 max-w-md text-lg text-navy-700">My wellbeing. My choices. My next step.</p>
          <Link to="/courses">
            <Button data-testid="hero-cta" className="mt-6 w-fit rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600 shadow-ambient">
              Build a new tool <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {statConfig.map((s) => (
          <Link to={s.to} key={s.key} data-testid={`stat-${s.key}`}>
            <Card className="group h-full rounded-2xl border-navy-900/5 bg-white p-6 shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.tint}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-5 font-serif text-4xl font-bold text-navy-900">
                {stats ? (stats[s.key] ?? 0) : "—"}
              </div>
              <div className="mt-1 text-sm font-semibold text-navy-800">{s.label}</div>
              <div className="text-xs text-navy-400">{stats ? s.sub(stats) : ""}</div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Journey banner + GHL status */}
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="relative overflow-hidden rounded-3xl border border-gold-100/70 shadow-ambient lg:col-span-2">
          <img
            src="https://images.unsplash.com/photo-1533162507191-d90c625b2640?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
            alt="Zen stones"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FBF6EC] via-[#FBF6EC]/85 to-[#FBF6EC]/10" />
          <div className="relative flex h-56 flex-col justify-center p-8">
            <div className="flex items-center gap-2 text-gold-600">
              <Sparkles className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-widest">Continue Your Journey</span>
            </div>
            <h3 className="mt-2 font-serif text-2xl font-bold text-navy-900">Shape your session packages</h3>
            <p className="mt-2 max-w-sm text-navy-500">Create flexible pricing tables and plans your clients can explore in their portal.</p>
            <Link to="/plans">
              <Button data-testid="journey-cta" className="mt-4 w-fit rounded-full bg-navy-900 px-6 text-white hover:bg-navy-800">
                View Plans & Packages
              </Button>
            </Link>
          </div>
        </div>

        <Card className="relative overflow-hidden rounded-3xl border border-gold-100 bg-gradient-to-br from-gold-50 via-white to-white p-8 shadow-ambient">
          <img
            src="/leaf-corner.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-8 -right-8 h-44 w-44 rotate-[18deg] opacity-20 select-none"
          />
          <div className="relative">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-ambient">
              <PlugZap className="h-5 w-5 text-gold-600" />
            </div>
            <h3 className="mt-5 font-serif text-xl font-bold text-navy-900">GoHighLevel</h3>
            {stats?.ghl_connected ? (
              <div className="mt-3 flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm">Connected & syncing</span>
              </div>
            ) : (
              <p className="mt-3 text-sm text-navy-500">
                Connect your sub-account to sync appointments, invoices and client contacts securely.
              </p>
            )}
            <Link to="/settings">
              <Button data-testid="ghl-connect-cta" variant="secondary" className="mt-5 w-full rounded-full bg-gold-500 text-white hover:bg-gold-600 border-0">
                {stats?.ghl_connected ? "Manage connection" : "Connect GHL"}
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Quick links */}
      <div className="mt-10">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-widest text-navy-400">Quick Links</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
          {quickLinks.map((q) => (
            <Link to={q.to} key={q.to} data-testid={`quicklink-${q.label.toLowerCase()}`}>
              <Card className="flex flex-col items-center gap-2 rounded-2xl border-navy-900/5 bg-white p-5 text-center shadow-ambient transition-all duration-200 hover:-translate-y-1 hover:shadow-ambient-lg">
                <q.icon className="h-6 w-6 text-gold-600" />
                <span className="text-xs font-medium text-navy-700">{q.label}</span>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
